import { GenericConfirmModal } from "@/components/common/GenericConfirmModal";
import { Colors } from "@/enums/Colors";
import { LazoThreadConversationStatus } from "@/enums/LazoThreadConversationStatus";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { LazoChatConversationShell } from "@/models/ai-chat/LazoChatConversation";
import { LazoChatTypingDTO } from "@/models/ai-chat/LazoChatTyping";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import { ExternalId } from "@/types/ExternalId";
import { CheckCircle, CheckCircleOutline, DraftsOutlined, MailOutline } from "@mui/icons-material";
import { Badge, Box, Button, ListItem, ListItemText, Tooltip, Typography, TypographyProps, badgeClasses, styled } from "@mui/material";
import moment from "moment";
import React, { Dispatch, SetStateAction, useState } from "react";
import { PortalChatAvatar } from "./PortalChatAvatar";
import { mapToNewListWithUpdatedTarget } from "./PortalChatContext";

export const CONVERSATION_LIST_DATES_WIDTH = "2.875rem";

type Props = {
  chat: LazoChatConversationShell;
  onChatSelect: (chat: LazoChatConversationShell) => void;
  setChats: Dispatch<SetStateAction<LazoChatConversationShell[]>>;
  userTyping?: LazoChatTypingDTO;
  isSelected: boolean;
  showResponseTimes: boolean;
};

function formatAllMemberNames(externalNames: string | undefined, internalNames: string | undefined, max = 2): string {
  const ext = externalNames
    ? externalNames
        .split(",")
        .map((n) => n.trim())
        .filter(Boolean)
    : [];
  const int = internalNames
    ? internalNames
        .split(",")
        .map((n) => n.trim())
        .filter(Boolean)
    : [];
  const all = [...ext, ...int];
  if (all.length === 0) return "";
  if (all.length <= max) return all.join(", ");
  return `${all.slice(0, max).join(", ")} +${all.length - max}`;
}

function buildMembersTooltip(externalNames: string | undefined, internalNames: string | undefined): string {
  const ext = externalNames
    ? externalNames
        .split(",")
        .map((n) => n.trim())
        .filter(Boolean)
    : [];
  const int = internalNames
    ? internalNames
        .split(",")
        .map((n) => n.trim())
        .filter(Boolean)
    : [];
  if (ext.length + int.length <= 2) return "";
  const parts: string[] = [];
  if (ext.length) parts.push(ext.join(", "));
  if (int.length) parts.push(int.join(", "));
  return parts.join(" · ");
}

function arePropsEqual(oldProps: Props, newProps: Props): boolean {
  return (
    oldProps.chat === newProps.chat &&
    oldProps.isSelected === newProps.isSelected &&
    oldProps.userTyping === newProps.userTyping &&
    oldProps.onChatSelect === newProps.onChatSelect &&
    oldProps.setChats === newProps.setChats
  );
}

export const PortalChatListItem = React.memo(function PortalChatListItem({
  chat,
  onChatSelect,
  setChats,
  userTyping,
  isSelected,
  showResponseTimes,
}: Props) {
  const [marking, setMarking] = useState<boolean>(false);
  const [finalizing, setFinalizing] = useState<boolean>(false);

  const [conversationToFinalize, setConversationToFinalize] = useState<ExternalId | null>(null);
  const [confirmFinalizeAnchor, setConfirmFinalizeAnchor] = useState<HTMLButtonElement | null>(null);

  const hasUnreadMessages = chat.unreadMessagesCount > 0;
  const hasInternalUnreadMessages = chat.internalUnreadMessageCount > 0;

  const currentUser = AuthService.getUser()?.externalId;

  const isToday = chat.updatedAt ? moment(chat.updatedAt || "2022-01-01").isSame(Date.now(), "day") : false;

  const timeSinceStart = chat.currentThreadStarted ? moment(chat.currentThreadStarted).fromNow(true) : "-";
  const timeWithoutAnswer = chat.lastOpenTime ? moment(chat.lastOpenTime).fromNow(true) : "-";

  function onMarkAsUnread(ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    ev.preventDefault();
    ev.stopPropagation();

    if (!chat.externalId) {
      NotificationService.warn("No conversation selected");
      return;
    }

    setMarking(true);

    if (hasUnreadMessages) {
      LazoChatConversationService.readConversation(chat.externalId)
        .then((r) => {
          r.onSuccess(() => {
            setChats((prev) =>
              prev.map((p) => {
                if (p.externalId === chat.externalId) {
                  return { ...p, unreadMessagesCount: 0 };
                }
                return p;
              })
            );
          });
          r.onError((m) => {
            NotificationService.error(m);
          });
        })
        .finally(() => setMarking(false));
    } else {
      LazoChatConversationService.markAsUnread(chat.externalId)
        .then((r) => {
          r.onSuccess(() => {
            setChats((prev) =>
              prev.map((p) => {
                if (p.externalId === chat.externalId && p.unreadMessagesCount === 0) {
                  return { ...p, unreadMessagesCount: (p.unreadMessagesCount += 1) };
                }
                return p;
              })
            );
          });
          r.onError((m) => {
            NotificationService.error(m);
          });
        })
        .finally(() => setMarking(false));
    }
  }

  function onMarkClosed(ev: React.MouseEvent<HTMLButtonElement>, conv: LazoChatConversationShell) {
    ev.preventDefault();
    ev.stopPropagation();
    if (!conv.currentThreadStatus || conv.currentThreadStatus === LazoThreadConversationStatus.FINALIZED) {
      NotificationService.info("This conversation is already finalized");
      return;
    }
    setConfirmFinalizeAnchor(ev.currentTarget);
    setConversationToFinalize(conv.externalId);
  }

  function onConfirmFinalizeConversation() {
    setFinalizing(true);
    LazoChatConversationService.finalizeThread(conversationToFinalize)
      .then((r) => {
        r.onSuccess(() => {
          setConfirmFinalizeAnchor(null);
          setChats((c) =>
            mapToNewListWithUpdatedTarget(c, conversationToFinalize, { currentThreadStatus: LazoThreadConversationStatus.FINALIZED })
          );
        });
        r.onError((m) => NotificationService.error(m));
      })
      .finally(() => setFinalizing(false));
  }

  function onCloseConfirmFinalizeConversation() {
    setConversationToFinalize(null);
  }

  return (
    <>
      <ChatListItemContainer isSelected={isSelected} onClick={() => onChatSelect(chat)}>
        <PortalChatAvatar chat={chat} />

        <ListItemText
          primary={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Tooltip
                title={buildMembersTooltip(chat.externalMemberNames, chat.internalMemberNames) || chat.title}
                placement="top"
                enterDelay={600}
                disableInteractive
                arrow
              >
                <span>
                  <ConversationTitleTypography sx={{ fontWeight: isSelected ? 700 : 500 }}>
                    {chat.title || formatAllMemberNames(chat.externalMemberNames, chat.internalMemberNames)}
                  </ConversationTitleTypography>
                </span>
              </Tooltip>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                {showResponseTimes && (
                  <>
                    <TimeContainer>
                      <DateTypography textAlign="center">{timeSinceStart}</DateTypography>
                    </TimeContainer>
                    <TimeContainer>
                      <DateTypography textAlign="center">{timeWithoutAnswer}</DateTypography>
                    </TimeContainer>
                  </>
                )}
                <TimeContainer>
                  <DateTypography>{isToday ? moment(chat.updatedAt).format("HH:mm") : moment(chat.updatedAt).format("ddd")}</DateTypography>
                </TimeContainer>
              </Box>
            </Box>
          }
          secondary={
            <Box>
              {chat.tenant?.name && (
                <Typography
                  component="span"
                  sx={{ fontSize: "0.75rem", fontWeight: 400, color: Colors.LIGHT_GRAY_8A8E93, whiteSpace: "nowrap" }}
                >
                  {chat.tenant.name}
                </Typography>
              )}

              <Content>
                <ListItemSubtext>
                  {userTyping && userTyping.user.externalId !== currentUser && `${userTyping.user.firstName} is typing...`}
                </ListItemSubtext>
              </Content>
            </Box>
          }
          sx={{
            "& .MuiListItemText-secondary": {
              fontSize: "0.75rem",
              fontWeight: 400,
              lineHeight: "1.25rem",
              letterSpacing: "0em",
              textAlign: "left",
              color: Colors.LIGHT_GRAY_8A8E93,
            },
            "&.MuiListItemText-root": {
              margin: "0 !important",
            },
          }}
        />
        {!chat.readOnly && (
          <ActionsContainer>
            <Box display="flex" alignItems="center" gap="1rem">
              {hasInternalUnreadMessages && (
                <UnreadMessagesCountBadge className="orange" badgeContent={chat.internalUnreadMessageCount}></UnreadMessagesCountBadge>
              )}
              {hasUnreadMessages && <UnreadMessagesCountBadge badgeContent={chat.unreadMessagesCount}></UnreadMessagesCountBadge>}
            </Box>
            <Box className="action-buttons" display="flex" alignItems="center" gap="0.5rem">
              <Tooltip title={marking ? "Marking" : !hasUnreadMessages ? "Mark as unread" : "Mark as read"}>
                <StyledActionButton disabled={marking} disableRipple onClick={onMarkAsUnread}>
                  {marking ? (
                    <Spinner size={"1rem"} sx={{ color: Colors.LAZO_PURPLE }} />
                  ) : hasUnreadMessages ? (
                    <MailOutline sx={{ fontSize: "1.25rem" }} />
                  ) : (
                    <DraftsOutlined sx={{ fontSize: "1.25rem" }} />
                  )}
                </StyledActionButton>
              </Tooltip>
              <Tooltip
                title={
                  !chat.currentThreadStatus || chat.currentThreadStatus === LazoThreadConversationStatus.FINALIZED
                    ? "Conversation already closed"
                    : "Mark conversation as closed"
                }
              >
                <StyledActionButton disabled={marking} disableRipple onClick={(ev) => onMarkClosed(ev, chat)}>
                  {finalizing ? (
                    <Spinner size={"1rem"} sx={{ color: Colors.LAZO_PURPLE }} />
                  ) : chat.currentThreadStatus && chat.currentThreadStatus !== LazoThreadConversationStatus.FINALIZED ? (
                    <CheckCircleOutline sx={{ fontSize: "1.25rem" }} />
                  ) : (
                    <CheckCircle sx={{ fontSize: "1.25rem" }} />
                  )}
                </StyledActionButton>
              </Tooltip>
            </Box>
          </ActionsContainer>
        )}
      </ChatListItemContainer>
      <GenericConfirmModal
        text={"Are you sure you want to finalize this conversation thread?"}
        anchor={confirmFinalizeAnchor}
        setAnchorElement={setConfirmFinalizeAnchor}
        onClose={onCloseConfirmFinalizeConversation}
        onConfirm={onConfirmFinalizeConversation}
        loading={finalizing}
      />
    </>
  );
}, arePropsEqual);

const ChatListItemContainer = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})<{
  isSelected: boolean;
}>(({ theme, isSelected }) => ({
  margin: "0 !important",
  position: "relative",
  gap: "1rem",
  width: "100%",
  transition: theme.transitions.create(["background-color", "box-shadow"], {
    duration: theme.transitions.duration.short,
  }),
  "& .action-buttons": {
    opacity: 0,
    pointerEvents: "none",
    transition: "opacity 150ms ease",
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)",
    "& .action-buttons": {
      opacity: 1,
      pointerEvents: "auto",
    },
  },
  backgroundColor: isSelected ? Colors.LAZO_LIGHT_PURPLE : "transparent",
  borderLeft: isSelected ? `3px solid ${Colors.LAZO_PURPLE}` : "3px solid transparent",
  // padding: "0.75rem 1.5rem",
  height: "5rem",
  cursor: "pointer",
}));

const Content = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  gap: "1rem",
  alignItems: "center",
  justifyContent: "flex-start",
  fontSize: ".12px",
}));

export const UnreadMessagesCountBadge = styled(Badge)(() => ({
  [`& .${badgeClasses.badge}`]: {
    minWidth: "1.25rem",
    minHeight: "1.25rem",
    fontSize: "0.625rem",
    fontWeight: 700,
    color: Colors.WHITE,
    backgroundColor: Colors.DARK_GREY_3E3E3E,
  },
  "&.orange": {
    [`& .${badgeClasses.badge}`]: { backgroundColor: Colors.ACCENT_ORANGE, color: Colors.BLACK },
  },
}));

const DateTypography = styled(Typography)(() => ({
  fontSize: "0.625rem",
  fontWeight: 400,
  color: Colors.LIGHT_GRAY_8A8E93,
}));

const ListItemSubtext = styled(({ children, ...props }: TypographyProps) => (
  <Typography children={children} component={"span"} variant="body2" color="text.secondary" {...props} />
))(() => ({
  fontSize: "0.75rem",
  display: "flex",
  fontWeight: 400,
  whiteSpace: "nowrap",
  alignItems: "center",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "90%",
  maxHeight: "1.25rem",
}));

const StatusCircle = () => (
  <span
    style={{
      position: "absolute",
      top: "0.125rem",
      right: "0.125rem",
      transform: "translate(50%, -50%)",
      height: "0.925rem",
      width: "0.925rem",
      borderRadius: "50%",
      backgroundColor: "green",
      border: "2px solid white",
    }}
  />
);

const ConversationTitleTypography = styled(({ children, ...props }: TypographyProps) => (
  <Typography children={children} component={"span"} {...props} />
))(() => ({
  fontSize: "0.875rem",
  fontWeight: 500,
  maxWidth: "60%",
  color: Colors.BLACK_151517,
}));

const TimeContainer = styled(Box)(() => ({
  display: "flex",
  justifyContent: "center",
  minWidth: CONVERSATION_LIST_DATES_WIDTH,
  maxWidth: CONVERSATION_LIST_DATES_WIDTH,
  height: "100%",
  alignItems: "center",
}));

const ActionsContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  position: "absolute",
  gap: "0.5rem",
  right: 5,
  bottom: 5,
}));

const StyledActionButton = styled(Button)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "1.75rem",
  minHeight: "1.75rem",
  padding: "0.25rem",
  width: "1.750rem",
  height: "1.750rem",
  borderRadius: "100px",
  ":hover": {
    backgroundColor: Colors.LIGHT_GRAY_DEDEE1,
  },
}));

export default PortalChatListItem;
